import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import React, { useImperativeHandle, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useInsurerReportsStoreState } from "store/InsurerReportsStore";
import { InsurerReport, InsurerReportEntry } from "types/insurerReports";
import { formatDateWithTime } from "utils/pdf";
import {
  EntryColumnConfig,
  insurerReportEntriesColumnConfig,
} from "./insurerReportEntriesColumnConfig";
import { applyRedCellHighlights } from "utils/excelExportHighlights";

// The insurer report number lives on the parent report, not on the entry row, so
// it is prepended as the first Excel column and resolved via insurerReportId.
const insurerReportNumberColumn: EntryColumnConfig = {
  columnId: "insurerReportNumber",
  width: 160,
  reorderable: false,
  resizable: true,
  sortable: false,
  label: "pct.insurerReportNumber.label",
  cellType: "customtext",
};

// What the page holds a ref to. Saving goes through here so the grid's red
// mismatch cells are marked in the workbook before it is written.
export type ExportExcelHandle = { save: () => void };

type Props = {};

export const ExportExcel = React.forwardRef<ExportExcelHandle, Props>(
  (props, ref) => {
    const { t } = useTranslation();
    const {
      insurerReports,
      insurerReportEntries,
      insurerReportEntryMetadataMap,
    } = useInsurerReportsStoreState();
    const excelExportRef = useRef<ExcelExport>(null);

    const columns = useMemo(
      () => [
        insurerReportNumberColumn,
        ...insurerReportEntriesColumnConfig.filter(
          (c) => c.columnId !== "ordinalNumber" && c.columnId !== "options"
        ),
      ],
      []
    );

    // Export exactly what the grid shows: the rows the metadata map marks
    // visible (parent selected, not deleted, matching the active search).
    // `entries` stays parallel to `data` so the highlight step can find the
    // entry behind each exported row.
    const { data, entries } = useMemo(() => {
      const reportNumberById = new Map<number, string>(
        (insurerReports ?? []).map((report: InsurerReport) => [
          report.insurerReportId,
          report.insurerReportNumber,
        ])
      );

      const entries = (insurerReportEntries ?? []).filter(
        (entry: InsurerReportEntry) =>
          insurerReportEntryMetadataMap[entry.insurerReportEntryId]
            ?.visible === true
      );
      const data = entries.map((entry: InsurerReportEntry) =>
        columns.reduce((row: Record<string, any>, c) => {
          row[c.columnId] =
            c.columnId === "insurerReportNumber"
              ? reportNumberById.get(entry.insurerReportId) ?? ""
              : (entry as any)[c.columnId];
          return row;
        }, {})
      );
      return { data, entries };
    }, [
      columns,
      insurerReports,
      insurerReportEntries,
      insurerReportEntryMetadataMap,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        save: () => {
          const excelExport = excelExportRef.current;
          if (!excelExport) return;
          const workbook = excelExport.workbookOptions();
          applyRedCellHighlights(
            workbook,
            entries,
            columns.map((c) => c.additionalInfo?.redColorFn)
          );
          excelExport.save(workbook);
        },
      }),
      [entries, columns]
    );

    return (
      <ExcelExport
        data={data}
        ref={excelExportRef}
        fileName={`Insurer Report Entries ${formatDateWithTime(
          new Date()
        )}.xlsx`}
      >
        {columns.map((col) => {
          const isNumber = col.cellType === "customnumber";
          return (
            <ExcelExportColumn
              key={col.columnId}
              field={col.columnId}
              title={t(col.label)}
              cellOptions={{ format: isNumber ? "#,##0.00" : "text" }}
              width={col.width}
              headerCellOptions={{
                background: "#005aa1",
                color: "#fff",
                textAlign: isNumber ? "right" : "left",
              }}
            />
          );
        })}
      </ExcelExport>
    );
  }
);
